import React, { useEffect, useRef } from "react";
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from "react-icons/fa";

// Animation tuning
const SPIN_DEG_PER_SEC = 120;
const MARQUEE_PCT_PER_SEC = 100;
const EASE_SECONDS = 0.7;
// Matches the tonearm transition duration in styles.css
const ARM_SECONDS = 0.9;
// Thickness of the LIVE pill border (must match .live-marquee in styles.css)
const PILL_BORDER = 3;

/**
 * Radio player card: spinning vinyl, tonearm, volume and a sliding
 * "LIVE 24/7" pill that works as a window onto the page background.
 * Playback state lives in App; this component only renders and animates it.
 */
export default function Player({
  audioRef,
  isPlaying,
  togglePlay,
  volume,
  setVolume,
  muted,
  setMuted,
}) {
  const vinylRef = useRef(null);
  const marqueeRef = useRef(null);
  const anim = useRef({
    angle: 0,
    pos: 0,
    speed: 0,
    mv: 0,
    stop: null,
    playing: false,
    raf: 0,
  });

  // Speed eases up once the tonearm has landed and eases down when it lifts
  useEffect(() => {
    const a = anim.current;

    const startLoop = () => {
      if (a.raf) return;
      let last = performance.now();
      const tick = (now) => {
        const dt = Math.min((now - last) / 1000, 0.05);
        last = now;

        const target = a.playing ? 1 : 0;
        a.speed += (target - a.speed) * Math.min(1, (dt / EASE_SECONDS) * 3);
        if (!a.playing && a.speed < 0.003) a.speed = 0;

        if (a.playing) {
          a.stop = null;
          a.mv += (MARQUEE_PCT_PER_SEC - a.mv) * Math.min(1, (dt / EASE_SECONDS) * 3);
          a.pos += a.mv * dt;
          if (a.pos > 150) {
            a.pos -= 300;
            // The text is off screen at the wrap point, so swapping it is invisible
          }
        } else if (!a.stop && a.mv > 1) {
          // Decelerate over exactly the distance left to reach the center
          const minDist = a.mv * 0.5;
          let d = (((-a.pos) % 300) + 300) % 300;
          if (d < minDist) d += 300;
          a.stop = { start: a.pos, d, T: (2 * d) / a.mv, t: 0 };
        } else if (!a.stop) {
          a.mv = 0;
          a.pos = 0;
        }

        if (a.stop) {
          const s = a.stop;
          s.t += dt;
          const u = Math.min(1, s.t / s.T);
          a.pos = s.start + s.d * (1 - (1 - u) * (1 - u));
          a.mv = ((2 * s.d) / s.T) * (1 - u);
          if (u >= 1) {
            a.pos = 0;
            a.mv = 0;
            a.stop = null;
          }
        }

        // Vinyl shares the marquee's speed so both stop at the same moment
        a.angle =
          (a.angle + SPIN_DEG_PER_SEC * (a.mv / MARQUEE_PCT_PER_SEC) * dt) % 360;

        const shown = ((((a.pos + 150) % 300) + 300) % 300) - 150;
        if (vinylRef.current) {
          vinylRef.current.style.transform = `rotate(${a.angle}deg)`;
        }
        if (marqueeRef.current) {
          marqueeRef.current.style.transform = `translateX(${shown}%)`;
        }

        const active = a.playing || a.speed > 0 || a.stop || a.mv > 0;
        a.raf = active ? requestAnimationFrame(tick) : 0;
      };
      a.raf = requestAnimationFrame(tick);
    };

    if (isPlaying) {
      const timer = setTimeout(() => {
        a.playing = true;
        startLoop();
      }, ARM_SECONDS * 1000);
      return () => clearTimeout(timer);
    }

    a.playing = false;
    startLoop();
    return undefined;
  }, [isPlaying]);

  useEffect(() => {
    const a = anim.current;
    return () => {
      cancelAnimationFrame(a.raf);
      a.raf = 0;
    };
  }, []);

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !muted;
    setMuted(!muted);
  };

  const handleVolume = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (audioRef.current) audioRef.current.volume = v;
    // Moving the slider up unmutes
    if (v > 0 && muted) toggleMute();
  };

  const containerRef = useRef(null);
  const pillRef = useRef(null);

  // Cut a pill-shaped window through the card so the page background shows inside the pill
  useEffect(() => {
    const card = containerRef.current;
    const pill = pillRef.current;
    if (!card || !pill) return undefined;
    const update = () => {
      const c = card.getBoundingClientRect();
      const p = pill.getBoundingClientRect();
      const b = PILL_BORDER;
      const iw = Math.max(0, pill.offsetWidth - b * 2);
      const ih = Math.max(0, pill.offsetHeight - b * 2);
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${iw}' height='${ih}'><rect width='${iw}' height='${ih}' rx='${ih / 2}' fill='black'/></svg>`;
      card.style.setProperty("--hole", `url("data:image/svg+xml,${encodeURIComponent(svg)}")`);
      card.style.setProperty("--hole-size", `${iw}px ${ih}px`);
      card.style.setProperty("--hole-pos", `${p.left - c.left + b}px ${p.top - c.top + b}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(card);
    ro.observe(pill);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <div className="player-container" ref={containerRef}>
      <div className="station-info">
        <div className="track-info">
          <div className="live-marquee" ref={pillRef}>
            <span className="live-marquee-text" ref={marqueeRef}>
              LIVE 24/7
            </span>
          </div>
        </div>

        <div className="volume-control volume-control--top">
          <button
            className="mute-btn"
            onClick={toggleMute}
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? <FaVolumeMute size={24} /> : <FaVolumeUp size={24} />}
          </button>

          <input
            type="range"
            className="volume-slider"
            aria-label="Volume"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={handleVolume}
          />
        </div>

        <div className="album-art-wrap">
          <div className="album-art" ref={vinylRef}>
            <img src="/vinyl.png" alt="Spinning Vinyl" />
          </div>
          <svg
            className={`tonearm ${isPlaying ? "tonearm--playing" : ""}`}
            viewBox="0 0 60 200"
            aria-hidden="true"
          >
            <line x1="45" y1="175" x2="45" y2="70" stroke="#d9d9e3" strokeWidth="5" strokeLinecap="round" />
            <line x1="45" y1="70" x2="30" y2="18" stroke="#d9d9e3" strokeWidth="5" strokeLinecap="round" />
            <rect x="22" y="6" width="16" height="12" rx="3" fill="#e94560" transform="rotate(-16 30 18)" />
            <circle cx="45" cy="175" r="14" fill="#2b2b36" stroke="#d9d9e3" strokeWidth="3" />
            <circle cx="45" cy="175" r="5" fill="#d9d9e3" />
          </svg>
          <div className="controls">
            <button className="play-btn" onClick={togglePlay} aria-label={isPlaying ? "Pause stream" : "Play stream"}>
              {isPlaying ? <FaPause size={30} /> : <FaPlay size={30} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}